import { useEffect, useState } from 'react';
import useAuth from './useAuth';
import { hasComplimentaryAccess } from '../utils/entitlement';
import { useBillingClient } from '../client/client-hook';
import { ListSubscriptionsReply } from '../proto/billing-manager_pb';
// @ts-ignore - lib/billing is plain JS
import {
  CHECKOUT_LIST_SUBSCRIPTION_STATUSES,
  PLAN_BADGE_LIST_SUBSCRIPTION_STATUSES,
  isCanceledSubscriptionStatus,
  resolvePlanBadge
} from '../lib/billing';

export interface SubscriptionStatus {
  /** null while loading; then true iff billing shows a non-Free plan. */
  isPaid: boolean | null;
  /**
   * May this account USE the product? `isPaid`, OR complimentary access from
   * the 'Paid Member' role (staff and comped accounts, whose limits are set
   * by hand and who never buy a subscription).
   *
   * Gates read THIS; `isPaid` stays pure billing truth for the plan chip and
   * checkout, which must not think a comped account has a Stripe plan.
   * Complimentary access resolves immediately rather than waiting on billing,
   * so it is a boolean, never null.
   */
  isEntitled: boolean;
  /**
   * The user held a subscription before but doesn't now — canceled or
   * lapsed. Locked surfaces use this to greet a returning customer
   * ("welcome back / reactivate") instead of pitching a first purchase.
   * Always false while loading or while isPaid.
   */
  hadPreviousSub: boolean;
  /**
   * An existing subscription is past_due. These users must settle the
   * overdue invoice on the billing page — staging a fresh quick-subscribe
   * cart for them resolves to a zero-change plan and errors at payment.
   */
  hasPastDue: boolean;
}

interface Resolved {
  /** null when billing could not be reached — never the same as "unsubscribed". */
  isPaid: boolean | null;
  hadPreviousSub: boolean;
  hasPastDue: boolean;
}

// One in-flight/settled promise per user id, shared by every consumer.
// The sidebar renders on every page and the dashboard gate mounts alongside
// it; without this cache each of them would issue its own listSubscriptions
// on every navigation.
let cachedFor: string | null = null;
let cachedPromise: Promise<Resolved> | null = null;
const listeners = new Set<(v: Resolved) => void>();
// Billing came back unreachable: retry a few times rather than leaving every
// consumer on "still loading" forever.
const RETRY_MS = 5000;
const MAX_RETRIES = 3;
// Every mounted consumer's refetch closure. Lets invalidateSubscriptionStatus()
// actively refresh + re-broadcast: clearing the cache alone would notify nobody,
// because mounted consumers' effects only re-run on tcId change — the just-paid
// user would stay "Free" (locked dashboard, padlocks, banner) until a hard
// reload. A SET, not one slot: route components are lazy and mount after the
// layout's long-lived consumers, so a single slot was owned by the page and
// nulled on navigation, leaving invalidate a silent no-op.
const refetchers = new Set<() => void>();
// Monotonic: only the newest fetch may broadcast, so a slow earlier response
// cannot overwrite a fresher one.
let fetchSeq = 0;

/**
 * Refetch + broadcast to every mounted consumer. Call after anything that
 * changes subscriptions (checkout completion, cancellation).
 */
export function invalidateSubscriptionStatus() {
  cachedFor = null;
  cachedPromise = null;
  // One live consumer's fetch broadcasts to every listener, so the first is enough.
  const first = refetchers.values().next();
  if (!first.done) first.value();
}

/**
 * Billing-derived subscription state — the SAME derivation as the header's
 * plan chip and the dashboard's get-started gate. Never derived from the
 * 'Paid Member' role: this deployment grants that role to every fresh
 * signup, so it says nothing about whether the user actually bought
 * anything.
 *
 * Fetches with the checkout status list (includes canceled) so one call
 * answers both questions: "subscribed now?" (active/trialing subs through
 * resolvePlanBadge) and "was subscribed before?" (anything else on file).
 */
export function useSubscriptionStatus(): SubscriptionStatus {
  const { user } = useAuth();
  const getBillingClient = useBillingClient();
  const [status, setStatus] = useState<Resolved | null>(null);

  useEffect(() => {
    const tcId = user?.tcId;
    if (!tcId) return;
    let cancelled = false;
    let retries = 0;
    let retryTimer: number | undefined;

    const fetchNow = () => {
      const seq = ++fetchSeq;
      cachedFor = tcId;
      cachedPromise = getBillingClient()
        .then((client) => client.listSubscriptions({ userId: tcId, statusList: [...CHECKOUT_LIST_SUBSCRIPTION_STATUSES] }))
        .then((res) => {
          const subs = (res as ListSubscriptionsReply.AsObject).subscriptionsList || [];
          const badgeSubs = subs.filter((s: any) => PLAN_BADGE_LIST_SUBSCRIPTION_STATUSES.includes(String(s?.status ?? '').toLowerCase()));
          const isPaid = resolvePlanBadge(badgeSubs, user?.roles) !== 'Free';
          const hasPastDue = !isPaid && subs.some((s: any) => String(s?.status ?? '').toLowerCase() === 'past_due');
          // Lapsed means CANCELED, not "has any subscription on file". The old
          // test counted an active add-on-only subscriber (Analyzer, dedicated
          // environment) as a returning customer, so someone being billed today
          // was greeted with "welcome back — resubscribe".
          const hadPreviousSub = !isPaid && subs.some((sub: any) => isCanceledSubscriptionStatus(sub?.status));
          return { isPaid, hadPreviousSub, hasPastDue };
        })
        .catch((): Resolved => {
          // Billing unreachable: FAIL OPEN. Reporting `false` here was
          // indistinguishable from a genuine free account, so one 5xx or a
          // failed token refresh locked a paying customer out — frosted
          // dashboard, padlocked sidebar, "start your plan" banner, with no
          // dismiss. `null` reads as "still loading" everywhere, which shows
          // the product rather than a paywall.
          cachedFor = null;
          cachedPromise = null;
          return { isPaid: null, hadPreviousSub: false, hasPastDue: false };
        });
      const p = cachedPromise;
      p.then((v) => {
        // A superseded fetch must not publish; its answer is older than the
        // one already on screen.
        if (seq !== fetchSeq) return;
        listeners.forEach((l) => l(v));
        // Nothing retries while the user simply sits there, and consumers that
        // wait on a resolved value (the tour's landing jump) would stall on a
        // permanent null — so try again, a bounded number of times.
        if (v.isPaid === null && retries < MAX_RETRIES) {
          retries += 1;
          retryTimer = window.setTimeout(fetchNow, RETRY_MS);
        }
      });
    };

    // Register this mount so invalidateSubscriptionStatus() can push fresh
    // state to every mounted consumer (e.g. right after a purchase completes).
    refetchers.add(fetchNow);

    if (cachedFor !== tcId || !cachedPromise) fetchNow();

    const listener = (v: Resolved) => {
      if (!cancelled) setStatus(v);
    };
    listeners.add(listener);
    cachedPromise!.then(listener);

    return () => {
      cancelled = true;
      listeners.delete(listener);
      refetchers.delete(fetchNow);
      if (retryTimer) window.clearTimeout(retryTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.tcId]);

  const isPaid = status ? status.isPaid : null;
  return {
    isPaid,
    // Short-circuits billing: a comped account unlocks on the token alone and
    // never flashes the paywall while listSubscriptions is in flight.
    isEntitled: isPaid === true || hasComplimentaryAccess(user?.roles),
    hadPreviousSub: Boolean(status?.hadPreviousSub),
    hasPastDue: Boolean(status?.hasPastDue)
  };
}
